/* eslint-disable no-param-reassign */
/* eslint-disable camelcase */
import { camelCase, isEqual, snakeCase } from 'lodash';
import { logEvent, PRODUCT_AREAS } from '../../utils/events';
import {
  Branding,
  BrandingMultiTextAttribute,
  BrandingRangeAttribute,
  BrandingTextAttribute,
  BrandingValue,
  LogFormChangesProps,
  RangeAttributeBoundaries,
} from './types';

type BrandingValues = Record<string, BrandingValue>;

export function getValuesFromBranding(branding?: Branding): BrandingValues {
  if (!branding) return {};

  return branding.attributes.reduce((acc, { inputName, value }) => {
    acc[inputName] = value;
    return acc;
  }, {} as BrandingValues);
}

export function buildBrandingPayload(
  values: BrandingValues,
  rangeBoundaries: RangeAttributeBoundaries,
): any {
  const rangeAttributes: BrandingRangeAttribute[] = [];
  const textAttributes: BrandingTextAttribute[] = [];
  const multiTextAttributes: BrandingMultiTextAttribute[] = [];

  Object.entries(values).forEach(([inputName, value]) => {
    if (Array.isArray(value)) {
      return multiTextAttributes.push({ inputName, value });
    }

    if (typeof value === 'string') {
      return textAttributes.push({ inputName, value });
    }

    const range = rangeBoundaries.get(inputName);

    if (typeof value === 'number' && range) {
      return rangeAttributes.push({
        inputName,
        range,
        value,
      });
    }
  });

  return {
    rangeAttributes,
    textAttributes,
    multiTextAttributes,
  };
}

export function changeCase(object: any, casingFn: (s?: string) => string): any {
  if (
    !object ||
    object instanceof Date ||
    !['string', 'object'].includes(typeof object)
  ) {
    return object;
  }

  if (Array.isArray(object)) {
    return object.map((item) => {
      if (typeof item !== 'object') return item;
      return changeCase(item, casingFn);
    });
  }

  if (typeof object === 'object') {
    return Object.entries(object).reduce((acc: any, [key, value]) => {
      if (typeof value === 'object') {
        acc[casingFn(key)] = changeCase(value, casingFn);
        return acc;
      }

      acc[casingFn(key)] = value;
      return acc;
    }, {});
  }

  return casingFn(object);
}

export function toCamelCase(object: any): any {
  return changeCase(object, camelCase);
}

export function toSnakeCase(object: any): any {
  return changeCase(object, snakeCase);
}

export const logBrandFormUpdates = ({
  initialValues,
  updatedValues,
  shopId,
}: LogFormChangesProps) => {
  Object.keys(initialValues).forEach((key) => {
    const initialValue = initialValues[key];
    const updatedValue = updatedValues[key];

    if (!isEqual(initialValue, updatedValue)) {
      logEvent('brand center value updated', {
        product_area: PRODUCT_AREAS.BRAND_CENTER,
        input_name: key,
        initial_value: initialValue,
        updated_value: updatedValue,
        shop_id: shopId,
      });
    }
  });
};

export const breakpoints = {
  small: 768,
  large: 1024,
};
